"use client";

import { useEffect, useRef, useState } from "react";
import { isDocumentDefined } from "@huub-web/utils";

type IntersectionObserverCallback = (
  entries: IntersectionObserverEntry[],
  observer: IntersectionObserver,
) => void;

type UseIntersectionObserverOptions = IntersectionObserverInit & {
  triggerOnce?: boolean;
};

/**
 * Hook to observe when an element enters/exits the viewport using IntersectionObserver
 */
export const useIntersectionObserver = <T = HTMLElement>(
  callback?: IntersectionObserverCallback,
  options: UseIntersectionObserverOptions = {},
) => {
  const ref = useRef<T>(null);
  const observer = useRef<IntersectionObserver | null>(null);
  const [isIntersecting, setIsIntersecting] = useState(false);
  const hasTriggered = useRef(false);
  const callbackRef = useRef(callback);
  const optionsRef = useRef(options);

  callbackRef.current = callback;
  optionsRef.current = options;

  useEffect(() => {
    if (!ref.current || !isDocumentDefined()) {
      return;
    }

    const { triggerOnce = false, ...intersectionOptions } = optionsRef.current;

    const handleIntersection: IntersectionObserverCallback = (entries, obs) => {
      const [entry] = entries;
      const isCurrentlyIntersecting = entry.isIntersecting;

      setIsIntersecting(isCurrentlyIntersecting);

      // If triggerOnce is true and element is intersecting for the first time
      if (triggerOnce && isCurrentlyIntersecting && !hasTriggered.current) {
        hasTriggered.current = true;
        // Disconnect observer after first trigger
        obs.disconnect();
      }

      // Call user callback if provided
      if (callbackRef.current) {
        callbackRef.current(entries, obs);
      }
    };

    observer.current = new IntersectionObserver(handleIntersection, {
      threshold: 0.1,
      ...intersectionOptions,
    });

    observer.current.observe(ref.current as unknown as Element);

    // Check if element is already intersecting immediately after observing
    // This handles cases where the element is already in view when the observer starts
    const checkInitialIntersection = () => {
      if (observer.current && ref.current) {
        const rect = (
          ref.current as unknown as Element
        ).getBoundingClientRect();
        const viewportHeight =
          window.innerHeight || document.documentElement.clientHeight;
        const viewportWidth =
          window.innerWidth || document.documentElement.clientWidth;

        const threshold = intersectionOptions.threshold || 0.1;
        const thresholdValue = Array.isArray(threshold)
          ? threshold[0]
          : threshold;

        // Calculate if element meets the threshold requirement
        const elementHeight = rect.height;
        const elementWidth = rect.width;
        const visibleHeight = Math.max(
          0,
          Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0),
        );
        const visibleWidth = Math.max(
          0,
          Math.min(rect.right, viewportWidth) - Math.max(rect.left, 0),
        );
        const visibleArea = visibleHeight * visibleWidth;
        const totalArea = elementHeight * elementWidth;
        const intersectionRatio = totalArea > 0 ? visibleArea / totalArea : 0;

        const isCurrentlyIntersecting = intersectionRatio >= thresholdValue;

        if (isCurrentlyIntersecting) {
          setIsIntersecting(true);

          if (triggerOnce && !hasTriggered.current) {
            hasTriggered.current = true;
            observer.current?.disconnect();
          }

          // Create a mock entry for the callback
          if (callbackRef.current) {
            const mockEntry = {
              isIntersecting: true,
              intersectionRatio,
              boundingClientRect: rect,
              intersectionRect: rect,
              rootBounds: {
                top: 0,
                left: 0,
                bottom: viewportHeight,
                right: viewportWidth,
                width: viewportWidth,
                height: viewportHeight,
              },
              target: ref.current as unknown as Element,
              time: Date.now(),
            } as IntersectionObserverEntry;

            callbackRef.current([mockEntry], observer.current);
          }
        }
      }
    };

    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(checkInitialIntersection);

    return () => {
      if (observer.current) {
        observer.current.disconnect();
      }
    };
  }, []);

  return {
    ref,
    isIntersecting: optionsRef.current.triggerOnce
      ? hasTriggered.current || isIntersecting
      : isIntersecting,
  };
};
